import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { Inbox } from "../../../../api/types/inboxesTypes";

interface Props {
  inbox: Inbox;
  open: boolean;
  onClose: () => void;
  onDeleted: (inboxId: string) => void;
}

const deleteInbox = withErrorHandling(adminApi.deleteInbox);

const DeleteAgentInboxDialog = ({ inbox, open, onClose, onDeleted }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    const [resp, error] = await deleteInbox(inbox.id);
    setLoading(false);

    if (error || !resp.result) {
      logError(error, "[DeleteAgentInboxDialog] deleteInbox");
      sendNotificationError(error ? error.message : "Inbox could not be deleted");
      return;
    }

    sendNotification("Inbox deleted successfully");
    onDeleted(inbox.id);
    onClose();
  };

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Delete Inbox</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Delete {inbox.name}?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to delete this inbox. This action is permanent and can&apos;t be undone.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={loading} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteAgentInboxDialog;
